import React, { useEffect, useRef, useState } from 'react';
import { X, Check } from 'lucide-react';

const TheresTheTruth = () => {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    const makeObs = (ref, key, delay = 0) => {
      const obs = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) setTimeout(() => setVisibleItems(p => [...new Set([...p, key])]), delay);
        },
        { threshold: 0.1 }
      );
      if (ref.current) obs.observe(ref.current);
      return obs;
    };

    const h = makeObs(sectionRef, 'header');
    const l = makeObs(leftRef, 'left', 100);
    const r = makeObs(rightRef, 'right', 250);
    const c = makeObs(ctaRef, 'cta', 400);

    return () => { h.disconnect(); l.disconnect(); r.disconnect(); c.disconnect(); };
  }, []);

  const oldWay = [
    "Hope for brand deals",
    "Trade time for money",
    "Struggle with business operations",
    "Figure everything out alone",
    "Risk your reputation on untested products"
  ];

  const newWay = [
    "Own your revenue with digital products",
    "Build passive income streams",
    "Focus on content while we handle the business",
    "Partner with proven experts",
    "Launch with professional packaging and strategy"
  ];

  return (
    <section ref={sectionRef} className="py-24 px-6 bg-[rgb(17,17,19)] relative overflow-hidden">
      <div className="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] bg-[rgba(197,155,47,0.06)] blur-[120px] rounded-full pointer-events-none"></div>

      <div className="max-w-[1200px] mx-auto relative z-10">
        <div className={`text-center mb-16 transition-all duration-700 ${
          visibleItems.includes('header') ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-95'
        }`}>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Here's the <span className="bg-gradient-to-r from-[rgb(197,155,47)] to-[rgb(224,194,95)] text-transparent bg-clip-text">Truth</span>
          </h2>
          <p className="text-xl text-[rgb(218,218,218)] max-w-[700px] mx-auto">
            There's a better way to build your creator business
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Old Way */}
          <div ref={leftRef} className={`transition-all duration-700 ${
            visibleItems.includes('left') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-16'
          }`}>
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 h-full">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-red-500/20 border-2 border-red-500/40 flex items-center justify-center">
                  <X size={24} className="text-red-400" />
                </div>
                <h3 className="text-2xl font-bold text-white">The Old Way</h3>
              </div>
              <ul className="space-y-4">
                {oldWay.map((item, index) => (
                  <li key={index} className={`flex items-start space-x-3 transition-all duration-400 ${
                    visibleItems.includes('left') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                  }`} style={{ transitionDelay: `${100 + index * 80}ms` }}>
                    <X size={20} className="text-red-400 flex-shrink-0 mt-1" />
                    <span className="text-[rgb(218,218,218)]">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* New Way */}
          <div ref={rightRef} className={`transition-all duration-700 ${
            visibleItems.includes('right') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'
          }`}>
            <div className="bg-gradient-to-br from-[rgba(197,155,47,0.1)] to-[rgba(124,56,178,0.1)] border border-[rgb(197,155,47)] rounded-2xl p-8 h-full relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[rgb(124,56,178)] via-[rgb(197,155,47)] to-[rgb(124,56,178)]"></div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[rgba(197,155,47,0.3)] to-[rgba(124,56,178,0.3)] border-2 border-[rgb(197,155,47)] flex items-center justify-center">
                  <Check size={24} className="text-[rgb(197,155,47)]" />
                </div>
                <h3 className="text-2xl font-bold text-white">The New Way</h3>
              </div>
              <ul className="space-y-4">
                {newWay.map((item, index) => (
                  <li key={index} className={`flex items-start space-x-3 transition-all duration-400 ${
                    visibleItems.includes('right') ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                  }`} style={{ transitionDelay: `${100 + index * 80}ms` }}>
                    <Check size={20} className="text-[rgb(197,155,47)] flex-shrink-0 mt-1" />
                    <span className="text-[rgb(218,218,218)] font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div ref={ctaRef} className={`mt-12 text-center transition-all duration-700 ${
          visibleItems.includes('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <p className="text-2xl font-semibold text-white mb-3">
            Choose the path that <span className="text-[rgb(197,155,47)]">actually works</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default TheresTheTruth;
